import { apiClient } from "./client";
import { ApiResponse, Message } from "../types";

export interface ChatRequest {
  project_id: string;
  workspace_id: string;
  user_input: string;
  thread_id?: string;
}

export interface ChatResponse {
  message: Message;
  thread_id?: string;
}

export interface ResetSessionRequest {
  project_id: string;
}

export class ChatAPI {
  static async sendMessage(
    request: ChatRequest
  ): Promise<ApiResponse<ChatResponse>> {
    return apiClient.post<ChatResponse>("/chat/ask", request);
  }

  static async resetSession(
    request: ResetSessionRequest
  ): Promise<ApiResponse<void>> {
    const formData = new FormData();
    formData.append("project_id", request.project_id);

    return apiClient.postForm<void>("/chat/reset_session", formData);
  }

  static async *streamChat(
    request: ChatRequest,
    abortSignal?: AbortSignal
  ): AsyncGenerator<any, void, unknown> {
    let retryCount = 0;
    const maxRetries = 3;
    const retryDelay = 1000; // 1 second

    while (retryCount <= maxRetries) {
      try {
        const API_BASE_URL =
          process.env.REACT_APP_API_URL || window.location.origin;
        const streamUrl = `${API_BASE_URL}/v1/chat/ask`;
        console.log("Environment variables check:");
        console.log("  REACT_APP_API_URL:", process.env.REACT_APP_API_URL);
        console.log("  Resolved API_BASE_URL:", API_BASE_URL);
        console.log("  Final streamUrl:", streamUrl);

        // JSONデータとして送信（静的UIと同じ形式）
        const requestData = {
          user_input: request.user_input,
          project_id: request.project_id,
          workspace_id: request.workspace_id,
          thread_id: request.thread_id || "",
        };

        // AbortController for timeout handling
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          console.log("Stream timeout, aborting request");
          controller.abort();
        }, 30000); // 30 second timeout

        const response = await fetch(streamUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
            "Cache-Control": "no-cache",
          },
          body: JSON.stringify(requestData),
          credentials: "include",
          signal: abortSignal || controller.signal,
        });

        clearTimeout(timeoutId);

        console.log("Stream response status:", response.status);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error("No reader available");
        }

        let buffer = "";
        let hasReceivedData = false;
        let lastValidChunk: any = null;

        try {
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              console.log("Stream completed, hasReceivedData:", hasReceivedData);
              
              // ストリームが完了したが、最後の有効なチャンクがある場合は再送信
              if (lastValidChunk && !hasReceivedData) {
                console.log("Resending last valid chunk:", lastValidChunk);
                yield lastValidChunk;
              }
              break;
            }

            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;
            console.log("Received chunk:", chunk);

            const lines = buffer.split("\n");
            // Keep the last incomplete line in buffer
            buffer = lines.pop() || "";

            for (const line of lines) {
              console.log("Processing line:", line);
              if (line.startsWith("data: ")) {
                const data = line.slice(6).trim();
                console.log("Extracted data:", data);
                
                if (data === "[DONE]") {
                  console.log("Received [DONE] signal");
                  return;
                }

                if (data) {
                  try {
                    const parsed = JSON.parse(data);
                    console.log("Parsed data:", parsed);
                    lastValidChunk = parsed;
                    hasReceivedData = true;
                    yield parsed;
                  } catch (e) {
                    console.log("JSON parse error:", e, "for data:", data);
                    // Skip invalid JSON but don't break the stream
                    continue;
                  }
                }
              }
            }
          }
        } finally {
          reader.releaseLock();
        }

        // If we reach here, the stream completed successfully
        return;
      } catch (error) {
        console.error("Stream error (attempt", retryCount + 1, "):", error);

        if (retryCount < maxRetries) {
          console.log(`Retrying in ${retryDelay}ms...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          retryCount++;
          continue;
        }

        // If it's an abort error, don't retry
        if (error instanceof Error && error.name === 'AbortError') {
          console.error("Stream aborted due to timeout");
          throw new Error("Stream timeout - please try again");
        }

        throw error;
      }
    }
  }
}

export default ChatAPI;